/**
 * Centralised glossary for the Viatris Client Portal.
 *
 * Every definition lives here so copy stays consistent across the
 * Cohort Scoring Matrix, tier badges, status labels, and the various
 * lock / verdict explainers. Update copy in one place; every (i)
 * tooltip across the portal picks it up.
 *
 * Schema:
 *   { title: short header, body: full explanation, math?: bullet list,
 *     example?: short illustrative line }
 *
 * Used by:
 *   - components/DefinitionInfo.jsx (renders the click popover)
 *   - ViatrisClientDashboard.js (matrix headers, tier badges, locks)
 */

export const DEFS = {
  // ──────────────── Score columns ────────────────
  avg5: {
    title: 'Avg /5 — Committee Weighted Average',
    body: 'Weighted average of every 1–5 vote across the 9 evaluation domains and all 6 committee members. Partial submissions count — a specialist who only scored their own domain contributes their votes to the mean without dragging unscored dims to zero.',
    math: ['Avg /5 = Σ (every individual 1–5 vote) ÷ number of votes cast'],
    example: 'A PV reviewer only scored Safety = 4; a Regulatory reviewer only scored Time-to-Launch = 5; four other reviewers full-scored across all 9 → Avg = mean of all 38 numeric votes.',
  },
  verdict: {
    title: 'Verdict — Auto-derived Decision',
    body: 'The committee\'s consolidated decision word, automatically derived from the committee weighted Avg /5 once all 6 reviewers have submitted at least one score. Surfaces in the matrix and in the per-asset Deep Dive.',
    math: [
      'Pending          · no reviewer has submitted yet',
      'Under Review     · 1–5 of 6 reviewers in · verdict locked',
      'In-License       · all 6 in · Avg /5 ≥ 4.0 → move to term sheet / due diligence',
      'Reassess         · all 6 in · Avg /5 3.0 – 3.99 → hold for next milestone (data, regulatory, M&A)',
      'Decline          · all 6 in · Avg /5 < 3.0 → archive, do not pursue',
    ],
    example: 'Committee weighted Avg 3.6/5 → Reassess (hold for next data readout). Thresholds are a working proposal — subject to committee refinement after Q1 review.',
  },

  // ──────────────── Tier badges (math-anchored ranges) ────────────────
  // Thresholds mirror backend `_derive_recommendation` in
  // /app/backend/services/viatris_assessment.py — keep in sync.
  // Composite /45 conversions shown alongside so operators can
  // eyeball where any given asset sits without doing division in
  // their head.
  tier_strategic: {
    title: 'Strategic Priority',
    body: 'Top-tier asset — exceptional scientific case + strategic alignment with the client\'s declared focus. Reserved for the strongest opportunities of the cycle.',
    math: [
      'Weighted avg ≥ 4.5 · Composite ≥ 40.5 / 45',
      'AND no AI-flagged deal-killer (otherwise the AI can override to Not Suitable).',
    ],
  },
  tier_high: {
    title: 'High Priority',
    body: 'Strong asset worth deep committee discussion. Solid evidence, real differentiation, and a viable deal path.',
    math: ['Weighted avg 3.75 – 4.5 · Composite 33.75 – 40.5 / 45'],
  },
  tier_medium: {
    title: 'Medium',
    body: 'Promising but mixed evidence — typically held for re-evaluation after a milestone (data readout, regulatory event).',
    math: ['Weighted avg 3.0 – 3.75 · Composite 27 – 33.75 / 45'],
  },
  tier_monitor: {
    title: 'Monitor',
    body: 'Watch-only. The asset has merit but lacks one or more critical signals (clinical data depth, competitive whitespace, BD feasibility).',
    math: ['Weighted avg 2.0 – 3.0 · Composite 18 – 27 / 45'],
  },
  tier_low: {
    title: 'Low Priority',
    body: 'Weak fit — fundamentals are below the bar; would need a substantial inflection to merit committee action.',
    math: ['Weighted avg < 2.0 · Composite < 18 / 45'],
  },
  tier_not_suitable: {
    title: 'Not Suitable',
    body: 'Deal-killer flagged by the AI assessment — IP cliff, crowded competitive landscape, mis-aligned modality, or commercial fit. Surfaced even when the dimensional score is high so the committee can see WHY the AI parked it (click the (i) icon next to the badge). NOT a math threshold — a qualitative override.',
    math: ['Not Suitable is an AI qualitative override — not a math threshold. Click (i) for rationale.'],
  },
  tier_could_not_assess: {
    title: 'Could Not Assess',
    body: 'AI pipeline failure for this asset — typically insufficient public data, search failure, or schema mismatch in Claude\'s response. Not a verdict; the asset will be re-assessed in the next pipeline run.',
  },

  // ──────────────── Scoring Bands overview (one glance legend) ────────────────
  scoring_bands: {
    title: 'How assets get their tier',
    body: 'The displayed tier is an AI-holistic call — Claude reads the whole asset (indication, IP runway, competitive landscape, commercial fit, modality alignment) and picks the tier. The math anchors below are the SCORE-DERIVED baseline, but Claude can move an asset up or down that baseline when the qualitative case demands it. Where the displayed tier differs from the math anchor, an (i) chip next to the tier exposes Claude\'s specific rationale — so every deviation is defensible.',
    math: [
      'Math anchor bands (Weighted Avg / 5):',
      'Strategic Priority · ≥ 4.5',
      'High Priority     · 3.75 – 4.5',
      'Medium            · 3.0 – 3.75',
      'Monitor           · 2.0 – 3.0',
      'Low Priority      · < 2.0',
      'Not Suitable      · AI qualitative call (deal-killer, regardless of score)',
      'Could Not Assess  · AI pipeline could not evaluate (insufficient data / schema error)',
    ],
  },

  // ──────────────── Status labels (committee lifecycle) ────────────────
  status_pending: {
    title: 'Status: Pending',
    body: 'No committee member has scored this asset yet. It sits in the Committee Action Required queue.',
  },
  status_under_review: {
    title: 'Status: Under Review',
    body: 'At least one but fewer than 6 committee members have submitted scores. The Verdict + committee weighted average stay locked until all 6 have submitted at least one score (blinded Delphi).',
  },
  status_scored: {
    title: 'Status: Scored',
    body: 'All 6 committee members have submitted. The Verdict and all numeric scores have unlocked simultaneously.',
  },

  // ──────────────── Lock icon explainer ────────────────
  lock: {
    title: 'Hidden until all 6 reviewers submit',
    body: 'Blinded Delphi — every reviewer\'s individual scores and the committee weighted Avg / 5 stay hidden until each of the 6 members has submitted at least one score. This prevents anchoring on early submitters\' numbers. Once the 6th member submits, Avg / 5 and the Verdict unlock together.',
  },

  // ──────────────── AI concern explainer ────────────────
  ai_concern: {
    title: 'AI Concern — Math vs. Qualitative Disagreement',
    body: 'The AI flagged a tier mismatch: the dimensional score suggests one tier, but qualitative analysis (crowded competition, IP, commercial fit, strategic alignment) argues for another. This is exactly where the AI is doing more than averaging numbers.',
    example: 'Weighted avg 3.8 / 5 (math says High Priority) but Claude parked it in Not Suitable because of a crowded statin / PCSK9 space + China-only rights → click the (i) for the full rationale.',
  },

  // ──────────────── Deep Dive Scoring section ────────────────
  scoring_section: {
    title: 'Scoring · 9 Scientific Evaluation Domains',
    body: 'Each asset is scored 1–5 across 9 scientific domains by the committee. Each row below shows the score, a 0–100 % bar, the AI\'s rationale for that domain, and the AI\'s confidence (Low / Medium / High). The weighted average at the top is the mean over every scored vote — partial submissions from specialists (a Regulatory reviewer scoring only Regulatory, etc.) are treated fairly.',
    math: [
      'Weighted Avg / 5 = Σ (every scored 1–5 vote) ÷ number of votes cast',
      'Domains: Prevalence · Unmet Need · Efficacy · Safety · Competitive Differentiation · Time to Launch · Strategic Alignment · Remaining Investment Required · Clinical Adoption Potential',
    ],
  },

  // ──────────────── Deep Dive Latest Alerts ────────────────
  latest_alerts: {
    title: 'Latest Alerts · 90-day Signal Stream',
    body: 'Real-time intelligence signals surfaced by the ForesightRx alerts engine — news, regulatory events, clinical readouts, M&A activity, and IP/litigation moves tied to this asset\'s company in the last 90 days. Each alert carries a severity (Critical / High / Medium / Low) and a category (Clinical / Regulatory / Deal / IP / Earnings).',
    example: 'Critical: NMPA approval announced · High: Phase 3 readout positive · Medium: New licensing partnership · Low: Conference abstract published',
  },
};
